const DEFAULT_TIMEOUT_MS = 10000;

export class DocumentSettleError extends Error {
  constructor(reason, message) {
    super(message);
    this.name = 'DocumentSettleError';
    this.reason = reason;
  }
}

function angularScopeFor(element, angularApi) {
  if (!angularApi || typeof angularApi.element !== 'function') return null;

  const wrapped = angularApi.element(element);
  return wrapped.isolateScope?.() || wrapped.scope() || null;
}

function settlementTurn($timeout) {
  if (typeof $timeout === 'function') {
    // Angular's $timeout resolves after its callback and the resulting digest.
    return Promise.resolve($timeout(() => {}, 0));
  }

  return new Promise(resolve => setTimeout(resolve, 0));
}

async function waitForQuiescentTurn(rootScope, $timeout, mutationVersion) {
  while (rootScope && rootScope.$$phase) {
    await settlementTurn($timeout);
  }

  const beforeTurn = mutationVersion();
  await settlementTurn($timeout);
  // MutationObserver callbacks are microtasks; flush them before comparing.
  await Promise.resolve();

  return (!rootScope || !rootScope.$$phase) && beforeTurn === mutationVersion();
}

async function settleDocument(applyPromise, options) {
  const rootScope = options.$rootScope || null;
  const appScope =
    options.scope ||
    (rootScope && typeof rootScope.findAppScope === 'function'
      ? rootScope.findAppScope()
      : null);
  const root = options.root || document.querySelector('.lease.lease--original');
  const angularApi = options.angular || globalThis.angular;
  let mutations = 0;
  const observer =
    root && typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => {
          mutations += 1;
        })
      : null;

  if (!root) {
    throw new DocumentSettleError(
      'document_root_missing',
      'The rendered lease document could not be found.',
    );
  }

  observer?.observe(root, {
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  });
  options.registerCleanup?.(() => observer?.disconnect());

  try {
    await applyPromise;

    // This $timeout turn is load-bearing: applyValuesAndProcessChanges updates
    // the lease model before Angular has necessarily completed its next digest.
    await settlementTurn(options.$timeout);

    // lease-if watchers run in the digest above. Refreshing the manager applies
    // their final values to hidden branches before the projection is read.
    const leaseIfManager = options.leaseIfManager || globalThis.leaseIfManager;
    if (typeof leaseIfManager?.updateAllIfDisplay === 'function') {
      leaseIfManager.updateAllIfDisplay();
    }

    // generate-table owns its derived row model. Re-run calculateRows on every
    // RENDERED table after the imported rent terms have reached Angular scopes.
    //
    // Not every <generate-table> in the DOM is a live one. A table inside a
    // lease-if branch that is currently false is present but never linked, so it
    // has no scope and therefore no calculateRows. Treating that as a failure
    // aborted the entire second pass over a table that is not in the projection
    // anyway — the projection skips hidden subtrees (P6-09), so a table we
    // cannot reach is also a table the agent will never be shown.
    //
    // Skipped tables are reported once, with a locator, rather than swallowed:
    // a LIVE table that fails to recalculate would leave its atom label stale
    // ("2 rows, Year 1–Year 2") while the underlying rows changed, and that is a
    // wrong projection rather than a missing one. If this warning ever names a
    // visible table, that is a real defect and not noise.
    const generateTables = Array.from(root.querySelectorAll('generate-table'));
    const skippedTables = [];
    const tableCalculations = [];

    for (const table of generateTables) {
      const tableScope = angularScopeFor(table, angularApi);
      if (typeof tableScope?.calculateRows !== 'function') {
        skippedTables.push(table);
        continue;
      }
      // A throw from calculateRows itself is a real failure and still aborts:
      // that is a live table whose recalculation is broken.
      tableCalculations.push(tableScope.calculateRows());
    }

    if (skippedTables.length > 0 && typeof console !== 'undefined') {
      console.warn(
        `[settle] skipped ${skippedTables.length} of ${generateTables.length} generate-table element(s) with no linked scope — ` +
          'expected for tables inside a false lease-if branch, a defect if any of these are visible:',
        skippedTables.map(el => ({
          hidden: Boolean(el.closest('.lp-hide-element')),
          pid: el.closest('[pid]')?.getAttribute('pid') || null,
          html: el.outerHTML.slice(0, 120),
        })),
      );
    }

    await Promise.all(tableCalculations);
    await settlementTurn(options.$timeout);

    if (typeof appScope?.structureChanged === 'function') {
      await appScope.structureChanged();
    }
    if (typeof appScope?.compileVisibleFreeTexts === 'function') {
      await appScope.compileVisibleFreeTexts(root);
    }

    // Require one complete $timeout turn with neither a digest nor a DOM
    // mutation. If a recalculation schedules more work, continue until a turn
    // is genuinely quiet or the outer bounded timeout refuses the projection.
    let isQuiescent = false;
    while (!isQuiescent) {
      if (options.cancelled) {
        throw new DocumentSettleError(
          'document_settle_timeout',
          'The lease document did not settle before its deadline.',
        );
      }
      isQuiescent = await waitForQuiescentTurn(
        rootScope,
        options.$timeout,
        () => mutations,
      );
    }

    return root;
  } finally {
    observer?.disconnect();
  }
}

/**
 * Wait for imported deal terms and every document-derived rendering pass.
 * Refuses with DocumentSettleError rather than building a stale projection.
 */
export function awaitDocumentSettled(applyPromise, options = {}) {
  const timeoutMs = options.timeoutMs || DEFAULT_TIMEOUT_MS;
  let timeoutId;
  let cleanup = () => {};
  const settleOptions = {
    ...options,
    cancelled: false,
    registerCleanup(callback) {
      cleanup = callback;
    },
  };

  const timeout = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      settleOptions.cancelled = true;
      reject(
        new DocumentSettleError(
          'document_settle_timeout',
          `The lease document did not settle within ${timeoutMs}ms.`,
        ),
      );
    }, timeoutMs);
  });

  return Promise.race([
    settleDocument(applyPromise, settleOptions),
    timeout,
  ]).finally(() => {
    cleanup();
    clearTimeout(timeoutId);
  });
}
